import type { HTMLAttributes, ReactNode } from 'react';
import { ArrowRight2 } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import styles from './Breadcrumbs.module.css';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbsProps extends HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  /** Inject the router's Link (e.g. `(item, children) => <Link to={item.href}>{children}</Link>`). */
  renderLink?: (item: BreadcrumbItem & { href: string }, children: ReactNode) => ReactNode;
  label?: string;
}

export function Breadcrumbs({
  items,
  renderLink,
  label = 'Breadcrumb',
  className,
  ...rest
}: BreadcrumbsProps) {
  if (items.length === 0) return null;
  return (
    <nav aria-label={label} className={cx(styles.nav, className)} {...rest}>
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className={styles.item}>
              {isLast || !item.href ? (
                <span className={styles.current} aria-current={isLast ? 'page' : undefined}>
                  {item.label}
                </span>
              ) : renderLink ? (
                <span className={styles.link}>
                  {renderLink({ ...item, href: item.href }, item.label)}
                </span>
              ) : (
                <a className={styles.link} href={item.href}>
                  {item.label}
                </a>
              )}
              {!isLast && (
                <ArrowRight2
                  size={12}
                  color="currentColor"
                  className={styles.separator}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
